/***
 *
 * Component for Patch Management
 * @description
 *
 */

import React, { useEffect, useState } from 'react';
import { Select, Input, Row, Col, Space, Typography, Spin, message, Drawer } from 'antd';
import { Appointments, CoreScripts } from './../../../../models/';
import { ReportingDashboard, Card, Button, Skeleton } from 'soxo-bootstrap-core';
import AppLoader from '../../../../components/app-loader';

const { Option } = Select;
const { Text, Paragraph } = Typography;

const PatchManagement = (props) => {
    const [patchList, setPatchList] = useState([]);
    const [selectedPatch, setSelectedPatch] = useState(null);
    const [passKey, setPassKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);
    const [jsonDrawerVisible, setJsonDrawerVisible] = useState(false);
    const [patchJson, setPatchJson] = useState(null);

    const [showReport, setShowReport] = useState(false);

    const [executeLoading, setExecuteLoading] = useState(false);
    const [isExecuted, setIsExecuted] = useState(false);

    // Fetch patches
    const getPatches = async () => {
        setLoading(true);
        try {
            const result = await CoreScripts.getPatches();
            if (result?.data) setPatchList(result.data);
        } catch (error) {
            message.error('Failed to load patch files.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getPatches();
    }, []);

    // View patch
    const handleView = async () => {
        if (!selectedPatch) return;

        if (!passKey) return;

        setViewLoading(true);
        try {
            const response = await CoreScripts.viewPatch(selectedPatch, passKey);
            if (response?.data) {
                setPatchJson(response.data);
                setJsonDrawerVisible(true);
            } else {
                throw new Error('Invalid patch or pass key');
            }
        } catch (error) {
        } finally {
            setViewLoading(false);
        }
    };

    const handleExecute = async () => {
        if (!selectedPatch || !passKey) return;

        setExecuteLoading(true);
        try {
            const res = await CoreScripts.executePatch(selectedPatch, passKey);

            if (res) {
                if (res?.success) {
                    message.success(res?.message);
                } else {
                    message.error(res?.message);
                }
            }

            //  In both success/failure cases — show the ReportingDashboard
            setShowReport(true);
            setIsExecuted(true); //Disable Execute button after execution
        } catch (error) {
            // message.error('Failed to execute patch.');
            setShowReport(true);
            setIsExecuted(true); //  Disable even if error occurs
        } finally {
            setExecuteLoading(false);
        }
    };

    return (
        <>
            <Card className="patch-management" bordered={false}>
                {/* Loader Component  */}
                <AppLoader visible={loading} />

                <Row gutter={[16, 16]} align="middle" justify="start" style={{ padding: '8px 0' }}>
                    {/* Patch selection list */}
                    <Col xs={24} sm={10} md={8}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Text type="secondary" style={{ marginBottom: 4, fontSize: 12 }}>
                                PATCH
                            </Text>
                            <Select
                                showSearch
                                placeholder="Select patch file"
                                value={selectedPatch}
                                onChange={(value) => {
                                    setSelectedPatch(value);
                                    setIsExecuted(false); //  reset execute/view buttons
                                    setShowReport();
                                }}
                                style={{ width: '100%' }}
                                filterOption={(input, option) => option?.children.toLowerCase().includes(input.toLowerCase())}
                            >
                                {patchList.map((patch, index) => (
                                    <Option key={index} value={patch.filename || patch}>
                                        {patch.filename || patch}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Col>

                    {/* Pass Key  */}
                    <Col xs={24} sm={8} md={6}>
                        <div className="action-keys">
                            <Text type="secondary" style={{ marginBottom: 4, fontSize: 12 }}>
                                Pass Key
                            </Text>
                            <Input
                                placeholder="Enter pass key"
                                value={passKey}
                                onChange={(e) => {
                                    setPassKey(e.target.value);
                                    setIsExecuted(false); //  reset execute/view buttons
                                    setShowReport();
                                }}
                                style={{ width: '100%' }}
                                maxLength={50}
                            />
                        </div>
                    </Col>

                    {/* Action Buttons for View and Execute */}
                    <Col xs={24} sm={6} md={6} style={{ marginTop: '20px' }}>
                        <Space>
                            <Button type="default" disabled={!selectedPatch || !passKey || isExecuted} loading={viewLoading} onClick={handleView}>
                                View
                            </Button>
                            <Button
                                disabled={!selectedPatch || !passKey || isExecuted}
                                type="primary"
                                loading={executeLoading}
                                onClick={handleExecute}
                            >
                                Execute
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {/* to load reporting dashbord with wit reporting id */}
                {showReport && <ReportingDashboard reportId={props.reportId}></ReportingDashboard>}
            </Card>

            {/* Drawer to display JSON */}
            <Drawer
                title={`Patch: ${selectedPatch}`}
                placement="right"
                width={600}
                onClose={() => setJsonDrawerVisible(false)}
                open={jsonDrawerVisible}
            >
                <Paragraph>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(patchJson, null, 2)}</pre>
                </Paragraph>
            </Drawer>
        </>
    );
};

export default PatchManagement;
